import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import type { AssistantCapabilities } from './assistantCapabilities';
import type { AssistantPlanner } from './planner';
import type { AssistantAgent } from './agent';
import type { GeocodeFn } from './geocode';
import AssistantChangePreview from './AssistantChangePreview';
import { ASSISTANT_ICON_URL, ASSISTANT_NAME } from './assistantIdentity';
import { EDGE_INSET, PANEL_BOTTOM, PANEL_HEIGHT } from './assistantLayout';
import { useAssistantConversation } from './useAssistantConversation';
import { captureAssistantPanel } from './captureAssistant';
import type { AssistantLocation } from './useAssistantConversation';
import type { AssistantMessage } from './types';

interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  /** Scopes the stored conversation to the signed-in user. */
  userId: string;
  /** Supplies status for scoped requests such as "all my active locations". */
  locations?: AssistantLocation[];
  /** What the assistant may do for the signed-in account. */
  capabilities?: AssistantCapabilities;
  /** Selects which skill answers a question; defaults to phrase matching. */
  planner?: AssistantPlanner;
  /**
   * Backend agent used to answer questions no local skill covers; defaults to a
   * disabled agent that never reaches the network.
   */
  agent?: AssistantAgent;
  /**
   * Resolves a place to coordinates for the marketplace location search;
   * defaults inside the hook to a geocoder that always declines.
   */
  geocode?: GeocodeFn;
  /** Called after a change is saved so the caller can refresh its list. */
  onApplied?: () => void;
}

/**
 * Renders `**bold**` segments without dangerouslySetInnerHTML — message text can
 * include location names that came from the API.
 */
const renderText = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((segment, index) =>
    segment.startsWith('**') && segment.endsWith('**') ? (
      <strong key={index}>{segment.slice(2, -2)}</strong>
    ) : (
      <span key={index}>{segment}</span>
    ),
  );

/** Avatar width plus its gap, so attachments line up with the message card. */
const AVATAR_GUTTER = 36;

/** Light-grey chip, matching the option buttons inside the reference cards. */
const CHIP_SX = {
  borderRadius: 2,
  textTransform: 'none',
  backgroundColor: '#F2F4F7',
  color: '#1E2A38',
  border: 'none',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#E6EAF0', border: 'none' },
} as const;

const HEADER_ICON_SX = {
  borderRadius: 1.5,
  backgroundColor: '#F2F4F7',
  color: '#5E7782',
  '&:hover': { backgroundColor: '#E6EAF0' },
} as const;

const ACTION_SX = { borderRadius: 2, textTransform: 'none' } as const;

/** White surface for content that sits directly on the panel's blue body. */
const CARD_SX = {
  px: 2,
  py: 1.75,
  borderRadius: 3,
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12)',
} as const;

/** Rounded-square rather than circular, so the square app icon is not cropped. */
const AssistantAvatar = ({ size = 28 }: { size?: number }) => (
  <Box
    component="img"
    src={ASSISTANT_ICON_URL}
    alt=""
    sx={{
      width: size,
      height: size,
      borderRadius: `${size * 0.25}px`,
      display: 'block',
      flexShrink: 0,
    }}
  />
);

const MessageBubble = ({ message }: { message: AssistantMessage }) => {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={(theme) => ({
            maxWidth: '85%',
            px: 1.75,
            py: 1.25,
            borderRadius: '14px 14px 4px 14px',
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
          })}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {renderText(message.text)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <AssistantAvatar />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: 2,
          py: 1.75,
          borderRadius: 3,
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12)',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1E2A38' }}>
          {renderText(message.text)}
        </Typography>
      </Box>
    </Box>
  );
};

interface DraftEditorProps {
  initialText: string;
  disabled: boolean;
  onUse: (text: string) => void;
  onCancel: () => void;
}

/**
 * Editable draft of proposed description copy.
 *
 * The assistant's drafting is rule-based and can only re-point the user's own
 * words, so the draft is always offered for correction rather than applied as
 * written.
 */
const DraftEditor = ({ initialText, disabled, onUse, onCancel }: DraftEditorProps) => {
  const [text, setText] = useState(initialText);

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={8}
        size="small"
        label="Text to add"
        value={text}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={disabled || !text.trim()}
          onClick={() => onUse(text)}
        >
          Use this
        </Button>
        <Button size="small" color="inherit" disabled={disabled} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

/**
 * Header subtitle, tailored to what the account can actually do. Only a supplier
 * owns and edits locations, so the "update your locations" framing is shown to
 * them alone; a buyer or admin gets a questions-first line, since offering them a
 * location edit they cannot perform would misdescribe the assistant.
 */
const subtitleFor = (capabilities?: AssistantCapabilities): string =>
  capabilities?.audience === 'supplier'
    ? 'Ask a question or update your locations'
    : 'Ask a question about your account';

/**
 * Conversational editor for the user's locations, opened from Manage Locations.
 *
 * The panel never mutates anything directly: it collects an instruction, shows
 * the server-computed dry run, and only applies after explicit confirmation.
 */
const AssistantPanel = ({
  open,
  onClose,
  userId,
  locations,
  capabilities,
  planner,
  agent,
  geocode,
  onApplied,
}: AssistantPanelProps) => {
  const navigate = useNavigate();
  const {
    messages,
    phase,
    input,
    examples,
    setInput,
    sendMessage,
    chooseLocation,
    acceptDraft,
    chooseStrategy,
    confirmApply,
    cancelPending,
    confirmNavigation,
    dismissNavigation,
    clearConversation,
  } = useAssistantConversation({
    userId,
    locations,
    capabilities,
    planner,
    agent,
    geocode,
    onApplied,
    onNavigate: navigate,
  });
  const endRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Admin-only export: staff testing the bot can copy/download a full
  // transcript to share in tickets without lossy screenshots.
  const canCapture = capabilities?.isStaff === true;
  const [captureLabel, setCaptureLabel] = useState('Copy conversation or download JSON');
  const captureResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const capturingRef = useRef(false);

  useEffect(() => () => clearTimeout(captureResetRef.current), []);

  const handleCapture = async () => {
    // Ignore repeated clicks while a capture is already running, so we never
    // spawn overlapping toBlob + clipboard/download operations.
    if (capturingRef.current) return;
    capturingRef.current = true;
    setCaptureLabel('Exporting…');
    try {
      const outcome = await captureAssistantPanel(messages);
      setCaptureLabel(
        outcome === 'copied'
          ? 'Copied conversation text'
          : outcome === 'downloaded'
            ? 'Downloaded conversation JSON'
            : 'Could not export',
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Sentry export failed', error);
      setCaptureLabel('Could not export');
    } finally {
      capturingRef.current = false;
    }
    clearTimeout(captureResetRef.current);
    captureResetRef.current = setTimeout(
      () => setCaptureLabel('Copy conversation or download JSON'),
      2500,
    );
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // The panel is non-modal, so nothing else is trapping Escape for it.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const busy = phase !== 'idle';
  const canSend = phase === 'idle' && input.trim().length > 0;
  // The assistant's range is not guessable from an empty box, so starter
  // prompts stand in for the capability list until the user has said anything.
  const showSuggestions = !busy && messages.length === 1;

  const handleSend = () => {
    if (!canSend) return;
    const value = input;
    setInput('');
    void sendMessage(value);
  };

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-label={ASSISTANT_NAME}
      // Non-modal: the page behind stays readable and usable, which is the
      // point of a floating widget rather than a drawer.
      aria-modal={false}
      sx={(theme) => ({
        position: 'fixed',
        right: { xs: EDGE_INSET.xs, sm: EDGE_INSET.sm },
        // Clears the launcher, which stays put and becomes the close control.
        // Derived from the launcher offset so the two cannot drift apart.
        bottom: { xs: PANEL_BOTTOM.xs, sm: PANEL_BOTTOM.sm },
        zIndex: theme.zIndex.drawer,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 3,
        backgroundColor: '#fff',
        boxShadow: '0 12px 40px rgba(16, 24, 40, 0.28)',
        width: expanded
          ? { xs: `calc(100vw - 24px)`, sm: 'min(900px, calc(100vw - 48px))' }
          : { xs: `calc(100vw - 24px)`, sm: 400 },
        // Expanded: anchor to the top edge as well, so the panel stretches to
        // fill the viewport between the top inset and the launcher. Collapsed:
        // stay bottom-anchored with a fixed height.
        top: expanded ? { xs: EDGE_INSET.xs, sm: EDGE_INSET.sm } : 'auto',
        height: expanded
          ? 'auto'
          : { xs: PANEL_HEIGHT.xs, sm: `min(640px, ${PANEL_HEIGHT.sm})` },
        transition: theme.transitions.create(['width', 'height', 'top'], {
          duration: theme.transitions.duration.shorter,
        }),
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      })}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <AssistantAvatar size={36} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {ASSISTANT_NAME}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5E7782' }} noWrap>
              {subtitleFor(capabilities)}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
          {canCapture && (
            <Tooltip title={captureLabel}>
              <span>
                <IconButton
                  size="small"
                  aria-label={captureLabel}
                  onClick={handleCapture}
                  sx={HEADER_ICON_SX}
                >
                  <DownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Clear conversation">
            <span>
              <IconButton
                size="small"
                aria-label="Clear conversation"
                disabled={phase === 'applying'}
                onClick={clearConversation}
                sx={HEADER_ICON_SX}
              >
                <DeleteSweepOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={expanded ? 'Shrink' : 'Expand'}>
            <IconButton
              size="small"
              aria-label={expanded ? 'Shrink assistant' : 'Expand assistant'}
              aria-pressed={expanded}
              onClick={() => setExpanded((value) => !value)}
              sx={HEADER_ICON_SX}
            >
              {expanded ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={`Close ${ASSISTANT_NAME}`}
            sx={HEADER_ICON_SX}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={(theme) => ({
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1.5,
          backgroundColor: theme.palette.primary.main,
        })}
        aria-live="polite"
      >
        <Stack spacing={1.5}>
          {messages.map((message) => {
            const { attachment } = message;
            // Bind the CTA link to a const so its guarded narrowing survives
            // into the Button's onClick closure (a property access would widen).
            const breakdownLink =
              attachment?.kind === 'breakdown' ? attachment.link : undefined;
            return (
              <Box key={message.id}>
                <MessageBubble message={message} />

                {/* Line attachments up with the card, clearing the avatar gutter. */}
                <Box sx={{ ml: message.role === 'assistant' ? `${AVATAR_GUTTER}px` : 0 }}>
                {attachment?.kind === 'preview' && (
                  <AssistantChangePreview preview={attachment.preview} />
                )}

                {attachment?.kind === 'breakdown' && (
                  <>
                    {attachment.rows.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          border: '1px solid #E3E8F0',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          overflow: 'hidden',
                        }}
                      >
                        {attachment.rows.map((row) => (
                          <Box
                            key={row.label}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                              px: 1.5,
                              py: 0.875,
                              borderBottom: '1px solid #F1F5F7',
                              '&:last-of-type': { borderBottom: 'none' },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                alignItems: 'flex-start',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#5E7782' }}>
                                {row.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, textAlign: 'right' }}
                              >
                                {row.value}
                              </Typography>
                            </Box>
                            {row.link && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  endIcon={<ArrowForwardIcon fontSize="small" />}
                                  sx={{ ...ACTION_SX, minHeight: 0, py: 0 }}
                                  onClick={() => {
                                    navigate(row.link!.path);
                                    onClose();
                                  }}
                                >
                                  {row.link.label}
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {breakdownLink && (
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        sx={{ ...ACTION_SX, mt: 1 }}
                        onClick={() => {
                          navigate(breakdownLink.path);
                          onClose();
                        }}
                      >
                        {breakdownLink.label}
                      </Button>
                    )}
                  </>
                )}

                {attachment?.kind === 'draft' && !message.resolved && (
                  <DraftEditor
                    initialText={attachment.text}
                    disabled={busy && phase !== 'drafting'}
                    onUse={(text) => void acceptDraft(text)}
                    onCancel={cancelPending}
                  />
                )}

                {attachment?.kind === 'choices' && !message.resolved && (
                  <Box sx={{ ...CARD_SX, mt: 1 }}>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {attachment.options.map((option, index) => (
                        <Button
                          key={option.strategy}
                          size="small"
                          variant={index === 0 ? 'contained' : 'text'}
                          disableElevation
                          sx={index === 0 ? ACTION_SX : CHIP_SX}
                          disabled={busy && phase !== 'resolving'}
                          onClick={() => chooseStrategy(option.strategy)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                )}

                {attachment?.kind === 'locations' && !message.resolved && (
                  <Box sx={{ ...CARD_SX, mt: 1 }}>
                    <Stack spacing={1}>
                      {attachment.candidates.map((candidate) => (
                        <Button
                          key={candidate.id}
                          size="small"
                          variant="text"
                          sx={CHIP_SX}
                          disabled={busy && phase !== 'targeting'}
                          onClick={() => void chooseLocation(candidate.id)}
                        >
                          {candidate.name}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                )}

                {attachment?.kind === 'confirm' && !message.resolved && (
                  <Box sx={{ ...CARD_SX, mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        sx={ACTION_SX}
                        disabled={phase === 'applying'}
                        onClick={() => void confirmApply()}
                      >
                        {phase === 'applying'
                          ? 'Saving…'
                          : `Save ${attachment.fieldsChanged} ${attachment.fieldsChanged === 1 ? 'change' : 'changes'}`}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        sx={CHIP_SX}
                        disabled={phase === 'applying'}
                        onClick={cancelPending}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                )}
                {attachment?.kind === 'navigate' && !message.resolved && (
                  <Box sx={{ ...CARD_SX, mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        sx={ACTION_SX}
                        onClick={() => {
                          confirmNavigation(attachment.path);
                          onClose();
                        }}
                      >
                        {attachment.label}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        sx={CHIP_SX}
                        onClick={dismissNavigation}
                      >
                        No thanks
                      </Button>
                    </Stack>
                  </Box>
                )}
                </Box>
              </Box>
            );
          })}

          {showSuggestions && (
            <Box sx={{ ...CARD_SX, ml: `${AVATAR_GUTTER}px` }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#1E2A38', mb: 1 }}
              >
                Try one of these
              </Typography>
              <Stack spacing={1}>
                {examples.starters.map((suggestion) => (
                  <Button
                    key={suggestion}
                    size="small"
                    variant="text"
                    sx={CHIP_SX}
                    onClick={() => void sendMessage(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {(phase === 'thinking' ||
            phase === 'answering' ||
            phase === 'validating' ||
            phase === 'applying') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: `${AVATAR_GUTTER}px` }}>
              <CircularProgress size={14} sx={{ color: '#fff' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Working…
              </Typography>
            </Box>
          )}
          <div ref={endRef} />
        </Stack>
      </Box>

      <Box component="footer" sx={{ borderTop: '1px solid #E3E8F0', backgroundColor: '#fff' }}>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSend();
          }}
          sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: 1.5, pt: 1.5 }}
        >
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder={busy ? 'Working…' : `Ask ${ASSISTANT_NAME} something…`}
            // On the input itself, not the wrapper — the field has no visible label.
            inputProps={{ 'aria-label': `Message ${ASSISTANT_NAME}` }}
            value={input}
            disabled={busy}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: '#F8FAFC' } }}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={!canSend}
            aria-label="Send message"
            sx={{ mb: 0.25 }}
          >
            <SendOutlinedIcon />
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          component="p"
          sx={{ color: '#8A9BA8', textAlign: 'center', px: 2, pb: 1.25, pt: 0.75 }}
        >
          {ASSISTANT_NAME} is AI-assisted — review the changes before saving.
        </Typography>
      </Box>
    </Box>
  );
};

export default AssistantPanel;
